import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Slider,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  Alert,
  Avatar,
  CircularProgress,
} from "@mui/material";
import InventoryItemCard from "./InventoryItemCard";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../store";
import { EnterTournament } from "../store/user/actions";
import { SHOW_ALERT } from "../store/user/action-types";
import ForestIcon from "@mui/icons-material/Forest";
import PetsIcon from "@mui/icons-material/Pets";
import DiamondIcon from "@mui/icons-material/Diamond";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import "../pages/Tools.css"; // Reuse the wood-themed styling
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 50,
      damping: 10,
      staggerChildren: 0.2,
    },
  },
};

const childVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 50 } },
};

// URI prefix for NFT images
const uri_prefix = "https://ipfs.io/ipfs/";

export const TournamentCardView = ({ id, back }: any) => {
  const dispatch = useDispatch<any>();
  const { loginInfo, gameStatus } = useSelector((state: AppState) => state.userModule);

  const [fawnNfts, setFawnNfts] = useState<any[]>([]);
  const [selectedNfts, setSelectedNfts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [value, setValue] = useState(0);

  const tournament = gameStatus?.tournaments?.find((item: any) => item.id === id);
  const tournament_rules = tournament?.rules || {};
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);

    const allTournaments = gameStatus?.tournaments || [];
    const enteredAssetIds: string[] = [];
    for (const t of allTournaments) {
      if (Array.isArray(t.entries)) {
        for (const entry of t.entries) {
          if (entry.asset_ids && Array.isArray(entry.asset_ids)) {
            enteredAssetIds.push(...entry.asset_ids);
          }
        }
      }
    }

    const schema_required = tournament_rules?.schema;
    const min_level = tournament_rules?.min_level ?? 1;
    const max_level = tournament_rules?.max_level ?? 1;

    let filteredFawns: any[] = [];
    if (schema_required && loginInfo.assets) {
      filteredFawns = loginInfo.assets.filter((item: any) => {
      const schemaName = item?.schema?.schema_name;
      const level = Number(item?.mutable_data?.Level) || 1;
      return (
        schema_required.includes(schemaName) &&
        level >= min_level &&
        level <= max_level
      );
      });
    }

    filteredFawns = filteredFawns.filter(
      (item: any) => !enteredAssetIds.includes(item.asset_id)
    );
    setFawnNfts(filteredFawns);

    setSelectedNfts((prevSelected) =>
      filteredFawns
        .filter((item: any) => prevSelected.includes(item.asset_id))
        .map((item: any) => item.asset_id)
    );

    setValue((prev) => Math.min(prev, filteredFawns.length));
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo.assets, tournament_rules?.schema, tournament_rules?.min_level, tournament_rules?.max_level]);

  useEffect(() => {
    if (value > fawnNfts.length) {
      setValue(fawnNfts.length);
      setSelectedNfts(fawnNfts.map((item: any) => item.asset_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fawnNfts]);
  const EnterTour = async () => {
    if (!selectedNfts.length) return;
    await dispatch(EnterTournament(selectedNfts, id));
    dispatch({ type: SHOW_ALERT, payload: { type: "success", msg: "Successfully entered the tournament!", show: true } });
    back();
  };

  const onClickNFT = (asset_id: any) => {
    let _selectedFawn: any = [...selectedNfts];
    if (_selectedFawn.find((id: any) => id == asset_id)) {
      _selectedFawn = _selectedFawn.filter((id: any) => id != asset_id);
      setValue(value - 1);
    } else {
      _selectedFawn.push(asset_id);
      setValue(value + 1);
    }
    setSelectedNfts(_selectedFawn);
  };

  const onUpdateSelectedNFTs = (value: number) => {
    setValue(value);

    let _selectedFawn: any = [];

    fawnNfts?.map((item: any, index: number) => {
      if (index < value) _selectedFawn.push(item.asset_id);
    });

    setSelectedNfts(_selectedFawn);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress sx={{ color: '#e94c58' }} />
      </Box>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ width: "100%" }}
    >
      <Box sx={{ width: "100%" }}>
        {/* Tournament Header */}
        <Paper
          className="game-paper"
          sx={{
            p: 3,
            mb: 4,
            position: 'relative',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #3d4a0b 0%, #2c3d0a 100%)',
            border: '4px solid #2eca6a',
            boxShadow: '0 8px 32px 0 rgba(46,202,106,0.15)',
            overflow: 'hidden',
          }}
        >
          {/* Animated Trophy */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              width: '100%',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 0 },
            }}
          >
            <motion.div
              initial={{ scale: 0.8, rotate: -10, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 120, damping: 8 }}
              style={{
                marginRight: 16,
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '50%',
                padding: 12,
                boxShadow: '0 4px 16px 0 rgba(255,204,51,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EmojiEventsIcon fontSize="large" sx={{ color: '#ffcc33', fontSize: 48 }} />
            </motion.div>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                pl: { xs: 0, md: 4 },
                width: '100%',
                gap: { xs: 2, md: 0 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="white"
                  mb={1}
                  letterSpacing={1}
                  align="left"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  Tournament #{id}
                </Typography>
                <Typography
                  variant="body1"
                  color="rgba(255,255,255,0.85)"
                  align="left"
                  sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
                >
                  Enter your best Deer and compete for <b style={{ color: "#ffad46" }}>Antlers</b> and glory!
                </Typography>
                {/* Responsive Chip Section: Rules */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  mt={2}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    rowGap: { xs: 1, sm: 1 },
                    columnGap: { xs: 0, sm: 1 },
                    width: '100%',
                  }}
                >
                  <Chip
                    label={`Min Level: ${tournament_rules?.min_level ?? 1}`}
                    sx={{
                      bgcolor: '#2eca6a',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: 13, sm: 15, md: 16 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: 'flex-start',
                    }}
                    icon={<ForestIcon sx={{ color: 'black !important' }} />}
                  />
                  <Chip
                    label={`Max Level: ${tournament_rules?.max_level ?? 1}`}
                    sx={{
                      bgcolor: '#e94c58',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: 13, sm: 15, md: 16 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: 'flex-start',
                    }}
                    icon={<ForestIcon sx={{ color: 'black !important' }} />}
                  />
                  <Chip
                    label={
                      tournament_rules?.schema?.length
                        ? `Allowed: ${tournament_rules.schema.join(", ")}`
                        : "Any Schema"
                    }
                    sx={{
                      bgcolor: '#29b6f6',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: 13, sm: 15, md: 16 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: 'flex-start',
                    }}
                    icon={<PetsIcon sx={{ color: 'black !important' }} />}
                  />
                </Stack>
              </Box>
              {/* Right column: Prize Pool and Entry Fee stacked, now responsive */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row', md: 'column' },
                  alignItems: { xs: 'stretch', sm: 'flex-start', md: 'center' },
                  justifyContent: { xs: 'flex-start', sm: 'space-between', md: 'flex-start' },
                  width: { xs: '100%', md: 'fit-content' },
                  mt: { xs: 2, md: 0 },
                  ml: { xs: 0, md: 4 },
                  minWidth: 0,
                  gap: { xs: 2, sm: 3, md: 0 },
                }}
              >
                {/* Prize Pool section responsive */}
                <Box sx={{ width: '100%', mb: 2 }}>
                  {tournament?.total_fees && parseFloat(tournament.total_fees) > 0 ? (() => {
                    const fee = parseFloat(tournament.total_fees).toFixed(2);
                    const prizePool = parseFloat(fee);
                    const prizes = [
                      {
                        label: `1st: ${(prizePool * 0.5).toFixed(2)} ANTLERS`,
                        color: '#ffcc33',
                        textColor: 'black',
                        icon: <EmojiEventsIcon sx={{ color: '#e94c58' }} />,
                      },
                      {
                        label: `2nd: ${(prizePool * 0.25).toFixed(2)} ANTLERS`,
                        color: '#b3e94c',
                        textColor: 'black',
                        icon: <EmojiEventsIcon sx={{ color: '#29b6f6' }} />,
                      },
                      {
                        label: `Burn 🔥: ${(prizePool * 0.1).toFixed(2)} ANTLERS`,
                        color: '#e94c58',
                        textColor: 'white',
                        icon: <DiamondIcon sx={{ color: 'white' }} />,
                      },
                    ];

                    return (
                      <>
                        <Chip
                          label={<span><b>Prize Pool: {tournament?.total_fees ? parseFloat(tournament.total_fees).toFixed(2) : '0.00'} ANTLERS</b></span>}
                          sx={{
                            bgcolor: '#ffad46',
                            color: 'black',
                            fontWeight: 'bold',
                            fontSize: { xs: 13, sm: 15, md: 16 },
                            width: { xs: '100%', sm: 'auto' },
                            mb: { xs: 1, sm: 1 },
                            justifyContent: 'flex-start',
                          }}
                          icon={<DiamondIcon sx={{ color: '#e94c58' }} />}
                        />
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          sx={{
                            mt: 0.5,
                            mb: 1.5,
                            flexWrap: 'wrap',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'center',
                            rowGap: 1,
                            columnGap: 1,
                          }}
                        >
                          {prizes.map((item, index) => (
                            <Chip
                              key={index}
                              label={<span><b>{item.label}</b></span>}
                              sx={{
                                bgcolor: item.color,
                                color: item.textColor,
                                fontWeight: 'bold',
                                fontSize: { xs: 13, sm: 15, md: 16 },
                                width: { xs: '100%', sm: 'auto' },
                                mb: { xs: 1, sm: 0 },
                                justifyContent: 'flex-start',
                              }}
                              icon={item.icon}
                            />
                          ))}
                        </Stack>
                        <Typography
                          variant="caption"
                          color="rgba(255,255,255,0.7)"
                          sx={{
                            mt: 0.5,
                            display: 'block',
                            fontWeight: 'bold',
                            textAlign: 'center',
                          }}
                        >
                          <b>Disclaimer:</b> The remaining prize pool is proportionally refunded to all participants.
                        </Typography>
                      </>
                    );
                  })() : (
                    <>
                      <Grid
                        container
                        spacing={2}
                        sx={{
                          mt: 0.5,
                          mb: 1.5,
                          alignItems: 'stretch',
                        }}
                      >
                        {['1st', '2nd', 'Burn'].map((label, i) => {
                          const colorSet = [
                            { bg: '#ffcc33', iconColor: '#e94c58', text: 'black' },
                            { bg: '#b3e94c', iconColor: '#29b6f6', text: 'black' },
                            { bg: '#e94c58', iconColor: 'white', text: 'white' },
                          ];
                          return (
                            <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: 'flex' }}>
                              <Chip
                                label={<span><b>{label}: 0.00 ANTLERS</b></span>}
                                sx={{
                                  bgcolor: colorSet[i].bg,
                                  color: colorSet[i].text,
                                  fontWeight: 'bold',
                                  fontSize: { xs: 11, sm: 13, md: 14 },
                                  width: '100%',
                                  mb: { xs: 1, sm: 0 },
                                  justifyContent: 'flex-start',
                                }}
                                icon={
                                  i < 2
                                    ? <EmojiEventsIcon sx={{ color: colorSet[i].iconColor }} />
                                    : <DiamondIcon sx={{ color: colorSet[i].iconColor }} />
                                }
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                      <Typography
                        variant="caption"
                        color="rgba(255,255,255,0.7)"
                        sx={{
                          mt: 0.5,
                          display: 'block',
                          fontWeight: 'bold',
                          textAlign: 'center',
                        }}
                      >
                        <b>Disclaimer:</b> The remaining prize pool is proportionally refunded to all participants.
                      </Typography>
                    </>
                  )}
                </Box>
                {/* Entry Fee chip below Prize Pool, responsive */}
                <Chip
                  label={
                    <span>
                      Entry Fee: <b>{(parseFloat(tournament?.fees || 0) * value).toFixed(2)} ANTLERS</b>
                    </span>
                  }
                  sx={{
                    bgcolor: '#ffad46',
                    color: 'black',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.1rem' },
                    py: { xs: 1, md: 2 },
                    px: { xs: 1.5, md: 2.5 },
                    boxShadow: '0 2px 8px 0 rgba(255,173,70,0.15)',
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    mt: 1,
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: 'center',
                  }}
                  icon={<DiamondIcon sx={{ color: 'black' }} />}
                />
                <Typography
                  variant="caption"
                  color="rgba(255,255,255,0.7)"
                  sx={{
                    mt: 1,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    maxWidth: 220,
                    width: '100%',
                    alignSelf: 'center',
                  }}
                >
                  <b>Note:</b> Entry fee is {parseFloat(tournament?.fees || 0).toFixed(2)} ANTLERS per Deer.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Selection Panel */}
        <Paper
          elevation={3}
          className="wood-panel"
          sx={{
            p: 3,
            mb: 4,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
            border: '4px solid #5c3c10',
            position: 'relative',
            boxShadow: '0 8px 32px 0 rgba(92,60,16,0.10)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h6" color="white" fontWeight="bold" gutterBottom>
              Select Your Deer for the Tournament
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.85)" sx={{ mb: 3 }}>
              Pick the Deer NFTs you want to enter. <b>Higher level Deer</b> have better chances of winning!
            </Typography>
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(41, 182, 246, 0.15)',
                color: 'white',
                '& .MuiAlert-icon': { color: '#29b6f6' },
                border: '1px solid rgba(41, 182, 246, 0.3)',
                fontWeight: 500,
                fontSize: 16
              }}
              icon={<PetsIcon />}
            >
              Use the <b>slider</b> for quick selection, or click on individual Deer cards below.
            </Alert>

            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="body2" color="white">0</Typography>
              <Slider
                min={0}
                max={fawnNfts?.length > 0 ? fawnNfts?.length : 0}
                onChange={(e, value: any) => onUpdateSelectedNFTs(value)}
                value={value}
                sx={{
                  flex: 1,
                  '& .MuiSlider-thumb': {
                    color: '#ffad46',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 8px 0 rgba(255,173,70,0.15)'
                  },
                  '& .MuiSlider-track': {
                    color: '#ffad46',
                  },
                  '& .MuiSlider-rail': {
                    color: 'rgba(255,255,255,0.3)',
                  }
                }}
                valueLabelDisplay="auto"
              />
              <Typography variant="body2" color="white">{fawnNfts?.length || 0}</Typography>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="body1" color="white" fontWeight="medium">
                <b>{value}</b> {value === 1 ? "Deer" : "Deer"} Selected
              </Typography>
              <Typography variant="body1" color="#ffad46" fontWeight="bold">
                Entry Fee: {parseFloat(tournament?.fees || 0)} ANTLERS
              </Typography>
            </Box>

            <motion.div
              whileHover={{ scale: selectedNfts.length === 0 ? 1 : 1.04 }}
              whileTap={{ scale: selectedNfts.length === 0 ? 1 : 0.98 }}
            >
              <Button
                variant="contained"
                fullWidth
                disabled={selectedNfts.length === 0}
                onClick={EnterTour}
                sx={{
                  backgroundColor: '#e94c58',
                  color: 'white',
                  '&:hover': { backgroundColor: '#d43545' },
                  '&.Mui-disabled': { backgroundColor: 'rgba(233, 76, 88, 0.3)', color: 'rgba(255,255,255,0.5)' },
                  borderRadius: '10px',
                  py: 1.7,
                  fontWeight: 'bold',
                  fontSize: 18,
                  letterSpacing: 1,
                  mb: 3,
                  boxShadow: selectedNfts.length === 0 ? 'none' : '0 4px 16px 0 rgba(233,76,88,0.15)'
                }}
                endIcon={<EmojiEventsIcon />}
              >
                Enter Tournament
              </Button>
            </motion.div>
          </Box>

          {/* Wood grain overlay */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
            opacity: 0.3,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
        </Paper>

        {/* NFT Selection Grid */}
        <Paper
          elevation={3}
          className="wood-panel"
          sx={{
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
            border: '4px solid #5c3c10',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px 0 rgba(92,60,16,0.10)',
          }}
        >
          <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            <Typography variant="h6" color="white" fontWeight="bold" gutterBottom>
              Your Available Deer
            </Typography>
            <Divider sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
          </Box>

          <Box
            sx={{
              maxHeight: "600px",
              overflowY: "auto",
              p: 2,
              position: 'relative',
              zIndex: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#5c3c10',
                borderRadius: '4px',
              },
            }}
          >
            {fawnNfts?.length > 0 ? (
              <Box
                sx={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  mt: 2,
                  p: 1,
                }}
              >
                <Grid
                  container
                  spacing={3}
                  columns={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}
                >
                  {fawnNfts.map((item: any) => {
                    const { asset_id, mutable_data, data, schema } = item;
                    return (
                      <InventoryItemCard
                        key={asset_id}
                        asset_id={asset_id}
                        level={mutable_data?.Level}
                        name={data?.name}
                        img_url={uri_prefix + data?.img}
                        isSelected={selectedNfts.includes(asset_id)}
                        onClick={onClickNFT}
                        schema={schema?.schema_name || ""}
                      />
                    );
                  })}
                </Grid>
              </Box>
            ) : (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <PetsIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
                <Typography variant="h6" color="white" gutterBottom>
                  No Deer NFTs Available
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: '600px', mx: 'auto' }}>
                  You don't have any Deer NFTs to enter in this tournament. <b>Visit the Marketplace</b> to acquire Deer NFTs.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Wood grain overlay */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
            opacity: 0.3,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
        </Paper>
      </Box>
    </motion.div>
  );
};
